'use client'

import { useCallback, useState } from 'react'
import jsQR from 'jsqr'
import { CalculatorCard, InfoBox, Disclaimer, SegmentedField } from './CalculatorUI'
import { CameraScanner, ResultPanel, UploadBox } from './ScannerShared'

const MAX_IMAGE_SIDE = 2000

// Decodes one frame or canvas: first as-is, then inverted (light-on-dark codes).
function decodeQR(canvas, ctx) {
  const { width, height } = canvas
  const data = ctx.getImageData(0, 0, width, height)
  const code = jsQR(data.data, width, height, { inversionAttempts: 'attemptBoth' })
  return code?.data || null
}

export default function QRCodeScanner() {
  const [mode, setMode] = useState('camera')
  const [preview, setPreview] = useState('')
  const [result, setResult] = useState('')
  const [error, setError] = useState('')

  const reset = () => {
    setPreview('')
    setResult('')
    setError('')
  }

  const handleFile = useCallback((file) => {
    if (!file) return
    setResult('')
    setError('')
    const reader = new FileReader()
    reader.onload = (e) => {
      const imgUrl = e.target.result
      setPreview(imgUrl)
      const img = new Image()
      img.onload = () => {
        // Very large photos are scaled down first so phones don't run out of memory.
        const scale = Math.min(1, MAX_IMAGE_SIDE / Math.max(img.width, img.height))
        const canvas = document.createElement('canvas')
        canvas.width = Math.round(img.width * scale)
        canvas.height = Math.round(img.height * scale)
        const ctx = canvas.getContext('2d', { willReadFrequently: true })
        ctx.drawImage(img, 0, 0, canvas.width, canvas.height)
        const text = decodeQR(canvas, ctx)
        if (text) setResult(text)
        else setError('No QR code could be found in this image. Try a clearer, closer or higher-resolution photo.')
      }
      img.onerror = () => setError('This file could not be read as an image.')
      img.src = imgUrl
    }
    reader.readAsDataURL(file)
  }, [])

  const handleCameraResult = useCallback((text) => {
    setPreview('')
    setError('')
    setResult(text)
  }, [])

  const isLink = /^https?:\/\//i.test(result)

  return (
    <CalculatorCard>
      <p className="text-xs font-bold uppercase tracking-wider text-[#0b2540]">Scan a QR code with your camera or from an image</p>

      <div className="mt-5 grid gap-6 lg:grid-cols-[minmax(0,1fr)_minmax(0,0.9fr)] lg:gap-8">
        <div className="min-w-0 space-y-5">
          <SegmentedField
            value={mode}
            onChange={(next) => {
              setMode(next)
              reset()
            }}
            options={[{ value: 'camera', label: 'Use camera' }, { value: 'upload', label: 'Upload image' }]}
          />

          {mode === 'camera' ? (
            <CameraScanner decodeFrame={decodeQR} onResult={handleCameraResult} onPhoto={handleFile} instruction="Point your camera at a QR code." />
          ) : (
            <UploadBox onFile={handleFile} noun="QR code" />
          )}

          <InfoBox
            items={[
              'The camera feed and images never leave your device — decoding happens entirely in your browser.',
              'Works best with a clear, well-lit, uncropped view of the QR code.',
              'If the camera is blocked, we will show you how to allow it — or just upload a photo instead.',
            ]}
          />
        </div>

        <ResultPanel preview={preview} result={result} error={error} label="Decoded content" onReset={reset} isLink={isLink} />
      </div>

      <Disclaimer>Only scan QR codes from sources you trust before opening any decoded link.</Disclaimer>
    </CalculatorCard>
  )
}
